import type { WhatsAppTemplateDto, WhatsAppTemplatePresentation } from "@multi-chat/shared";
import { voiceCallTtlMinutesToDays } from "./template-editor-presentation.js";
import {
  buildFormTemplatePresentation,
  templateDtoToFormState,
  type TemplateFormState,
} from "./template-management/template-management-form.js";
import {
  isTemplateDeletable,
  isTemplateEditable,
} from "./template-management/template-management-status.js";
import {
  resolveTemplateEditSurface,
} from "./template-management/template-editor-support.js";
import { englishTemplateLanguageLabel } from "./template-management/template-language-options.js";
import type { TemplateCreationUseCase } from "./template-management/template-creation-wizard-state.js";

export const TEMPLATE_LIST_FILTER_ALL = "all";

export interface TemplateListViewFilters {
  query: string;
  status: string;
  category: string;
  language: string;
}

export const emptyTemplateListFilters: TemplateListViewFilters = {
  query: "",
  status: TEMPLATE_LIST_FILTER_ALL,
  category: TEMPLATE_LIST_FILTER_ALL,
  language: TEMPLATE_LIST_FILTER_ALL,
};

export type TemplateListStatusTone =
  | "success"
  | "pending"
  | "warning"
  | "error"
  | "muted"
  | "default";

export type TemplateListStatusBadgeVariant =
  | "success"
  | "warning"
  | "error"
  | "default"
  | "outline";

export type TemplateListHeaderFormat = "TEXT" | "IMAGE" | "VIDEO" | "DOCUMENT" | "OTHER";

export type TemplateListComponentChip =
  | { kind: "header"; format: TemplateListHeaderFormat }
  | { kind: "body" }
  | { kind: "footer" }
  | { kind: "buttons"; count: number };

export interface TemplateListDetailLine {
  key: string;
  label: string;
  text: string;
}

const HEADER_FORMATS = new Set<TemplateListHeaderFormat>([
  "TEXT",
  "IMAGE",
  "VIDEO",
  "DOCUMENT",
]);

export function toggleTemplateStatusFilter(current: string, status: string): string {
  return normalizeTemplateListStatus(current) === normalizeTemplateListStatus(status)
    ? TEMPLATE_LIST_FILTER_ALL
    : normalizeTemplateListStatus(status);
}

export function normalizeTemplateListStatus(status: string): string {
  return status.trim().toUpperCase();
}

export interface TemplateLibraryStatusSummary {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
}

/** Exact status buckets. Unknown provider statuses stay in the total only. */
export function summarizeTemplateLibraryStatuses(
  statuses: readonly string[],
): TemplateLibraryStatusSummary {
  let approved = 0;
  let pending = 0;
  let rejected = 0;
  for (const status of statuses) {
    const normalized = normalizeTemplateListStatus(status);
    if (normalized === "APPROVED") approved += 1;
    else if (normalized === "PENDING") pending += 1;
    else if (normalized === "REJECTED") rejected += 1;
  }
  return { total: statuses.length, approved, pending, rejected };
}

export function templateListStatusTone(status: string): TemplateListStatusTone {
  switch (normalizeTemplateListStatus(status)) {
    case "APPROVED":
    case "ACTIVE":
      return "success";
    case "PENDING":
    case "PENDING_DELETION":
    case "IN_APPEAL":
      return "pending";
    case "PAUSED":
    case "FLAGGED":
      return "warning";
    case "REJECTED":
    case "DISABLED":
      return "error";
    case "DELETED":
      return "muted";
    default:
      return "default";
  }
}

export function templateListStatusBadgeVariant(
  status: string,
): TemplateListStatusBadgeVariant {
  const tone = templateListStatusTone(status);
  switch (tone) {
    case "success":
      return "success";
    case "pending":
    case "warning":
      return "warning";
    case "error":
      return "error";
    case "muted":
      return "outline";
    case "default":
      return "default";
    default: {
      const _exhaustive: never = tone;
      return _exhaustive;
    }
  }
}

export function templateListStatusLabelKey(status: string): string {
  return `whatsapp.templates.statusLabels.${normalizeTemplateListStatus(status)}`;
}

export function getTemplateListActionAvailability(status: string): {
  edit: boolean;
  delete: boolean;
} {
  return {
    edit: isTemplateEditable(status),
    delete: isTemplateDeletable(status),
  };
}

export function getTemplateListOverflowAvailability(
  canManage: boolean,
  status: string,
): {
  showMenu: boolean;
  edit: boolean;
  delete: boolean;
} {
  const availability = getTemplateListActionAvailability(status);
  const edit = canManage && availability.edit;
  const deleteAllowed = canManage && availability.delete;
  return {
    showMenu: edit || deleteAllowed,
    edit,
    delete: deleteAllowed,
  };
}

export function splitLanguageOptionLabel(
  label: string,
  code: string,
): { localized: string; code: string } {
  const suffix = ` (${code})`;
  if (label.endsWith(suffix) && label.length > suffix.length) {
    return { localized: label.slice(0, -suffix.length), code };
  }
  return { localized: label, code };
}

export function templateListCategoryI18nKey(category: string): string {
  return `whatsapp.templates.manage.categories.${category.trim().toUpperCase()}`;
}

export function formatTemplateListCategoryToken(category: string): string {
  const trimmed = category.trim();
  if (!trimmed) return trimmed;
  return trimmed
    .split(/[_-]+/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function componentType(component: Record<string, unknown>): string {
  return typeof component.type === "string" && component.type.trim().length > 0
    ? component.type.trim().toUpperCase()
    : "COMPONENT";
}

function headerFormat(component: Record<string, unknown>): TemplateListHeaderFormat {
  const raw =
    typeof component.format === "string" ? component.format.trim().toUpperCase() : "";
  if (HEADER_FORMATS.has(raw as TemplateListHeaderFormat)) {
    return raw as TemplateListHeaderFormat;
  }
  return "OTHER";
}

function buttonCount(component: Record<string, unknown>): number {
  return Array.isArray(component.buttons) ? component.buttons.length : 0;
}

export function summarizeTemplateListComponents(
  components: unknown,
): TemplateListComponentChip[] {
  if (!Array.isArray(components)) return [];

  const chips: TemplateListComponentChip[] = [];
  for (const entry of components) {
    const component = asRecord(entry);
    if (!component) continue;

    switch (componentType(component)) {
      case "HEADER":
        chips.push({ kind: "header", format: headerFormat(component) });
        break;
      case "BODY":
        chips.push({ kind: "body" });
        break;
      case "FOOTER":
        chips.push({ kind: "footer" });
        break;
      case "BUTTONS": {
        const count = buttonCount(component);
        if (count > 0) chips.push({ kind: "buttons", count });
        break;
      }
      default:
        break;
    }
  }
  return chips;
}

function collectComponentTexts(component: Record<string, unknown>): string[] {
  const texts: string[] = [];
  if (typeof component.text === "string" && component.text.trim()) {
    texts.push(component.text.trim());
  }
  if (typeof component.format === "string" && component.format.trim()) {
    texts.push(component.format.trim());
  }
  if (Array.isArray(component.buttons)) {
    for (const button of component.buttons) {
      const btn = asRecord(button);
      if (!btn) continue;
      const buttonText =
        typeof btn.text === "string"
          ? btn.text.trim()
          : typeof btn.type === "string"
            ? btn.type.trim()
            : "";
      if (buttonText) texts.push(buttonText);
    }
  }
  return texts;
}

export function templateListPreviewPresentation(
  template: WhatsAppTemplateDto,
): WhatsAppTemplatePresentation {
  return buildFormTemplatePresentation(templateDtoToFormState(template));
}

function uniqueSorted(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => value.trim().length > 0))].toSorted((a, b) =>
    a.localeCompare(b),
  );
}

export function collectTemplateListFilterOptions(items: readonly WhatsAppTemplateDto[]): {
  statuses: string[];
  categories: string[];
  languages: string[];
} {
  return {
    statuses: uniqueSorted(items.map((item) => normalizeTemplateListStatus(item.status))),
    categories: uniqueSorted(items.map((item) => item.category.trim().toUpperCase())),
    languages: uniqueSorted(items.map((item) => item.language)),
  };
}

export function hasActiveTemplateListFilters(filters: TemplateListViewFilters): boolean {
  return (
    filters.query.trim().length > 0 ||
    filters.status !== TEMPLATE_LIST_FILTER_ALL ||
    filters.category !== TEMPLATE_LIST_FILTER_ALL ||
    filters.language !== TEMPLATE_LIST_FILTER_ALL
  );
}

export function filterTemplateListItems(
  items: readonly WhatsAppTemplateDto[],
  filters: TemplateListViewFilters,
): WhatsAppTemplateDto[] {
  const query = filters.query.trim().toLowerCase();
  const status = filters.status === TEMPLATE_LIST_FILTER_ALL ? null : filters.status;
  const category =
    filters.category === TEMPLATE_LIST_FILTER_ALL ? null : filters.category.trim().toUpperCase();
  const language = filters.language === TEMPLATE_LIST_FILTER_ALL ? null : filters.language;

  return items.filter((item) => {
    if (status && normalizeTemplateListStatus(item.status) !== status) return false;
    if (category && item.category.trim().toUpperCase() !== category) return false;
    if (language && item.language !== language) return false;
    if (query.length === 0) return true;
    return (
      item.name.toLowerCase().includes(query) ||
      item.language.toLowerCase().includes(query) ||
      item.category.toLowerCase().includes(query)
    );
  });
}

export function templateListDetailLines(components: unknown): TemplateListDetailLine[] {
  if (!Array.isArray(components)) return [];

  const lines: TemplateListDetailLine[] = [];
  for (const [index, entry] of components.entries()) {
    const component = asRecord(entry);
    if (!component) continue;
    const texts = collectComponentTexts(component);
    if (texts.length === 0) continue;
    const type = componentType(component);
    lines.push({
      key: `${type}-${index}`,
      label: type,
      text: texts.join(" · "),
    });
  }
  return lines;
}

export type TemplateListDetailsButtonKind =
  | "QUICK_REPLY"
  | "URL"
  | "PHONE_NUMBER"
  | "VOICE_CALL"
  | "OTHER";

export interface TemplateListDetailsButton {
  key: string;
  kind: TemplateListDetailsButtonKind;
  /** i18n key under whatsapp.templates.list.detailPanel.buttonTypes.* */
  typeLabelKey: string;
  label: string;
  secondary?: string;
  secondaryKind?: "url" | "phone" | "ttl" | "raw";
}

export interface TemplateListDetailsContentBlock {
  key: string;
  kind: "header" | "body" | "footer" | "other";
  /** Primary human-readable text; may be empty for media-only headers. */
  text: string;
  /** Secondary metadata such as IMAGE / VIDEO. */
  meta?: string;
}

export interface TemplateListDetailsModel {
  preview: WhatsAppTemplatePresentation;
  parameterFormat: TemplateFormState["parameterFormat"];
  /** Create/Edit-aligned language label (Meta English display names). */
  languageLabel: string;
  /** i18n key under wizard.categories.* */
  categoryLabelKey: string;
  /** i18n key under wizard.useCases.*.title when Default editor applies; otherwise null. */
  useCaseTitleKey: string | null;
  /** i18n key for the Variable type field label (matches Create/Edit). */
  variableTypeFieldKey: string;
  /** i18n key for Named/Positional value (matches Create/Edit). */
  variableTypeValueKey: string;
  contentBlocks: TemplateListDetailsContentBlock[];
  buttons: TemplateListDetailsButton[];
  /** True when some Meta components cannot be shown in the polished content/buttons sections. */
  hasUnhandledComponents: boolean;
  editSurface: ReturnType<typeof resolveTemplateEditSurface>;
}

/** Same language presentation as Create/Edit Step 2 summary + language control. */
export function templateDetailsLanguageLabel(languageCode: string): string {
  return englishTemplateLanguageLabel(languageCode);
}

export function templateDetailsVariableTypeFieldKey(): string {
  return "whatsapp.templates.manage.wizard.editor.variableType";
}

export function templateDetailsVariableTypeValueKey(
  format: TemplateFormState["parameterFormat"],
): string {
  return format === "NAMED"
    ? "whatsapp.templates.manage.wizard.editor.variableNamed"
    : "whatsapp.templates.manage.wizard.editor.variablePositional";
}

/**
 * Infer Default use-case only when Edit already treats the template as the
 * shared MARKETING_DEFAULT-style Step-2 editor. Do not invent Catalog/Flow/etc.
 */
export function resolveTemplateDetailsUseCase(
  template: WhatsAppTemplateDto,
): TemplateCreationUseCase | null {
  const surface = resolveTemplateEditSurface(template);
  if (surface.kind !== "marketing_default_editor") return null;
  const category = template.category.trim().toUpperCase();
  if (category === "UTILITY") return "UTILITY_DEFAULT";
  if (category === "MARKETING") return "MARKETING_DEFAULT";
  return null;
}

export function templateDetailsCategoryLabelKey(category: string): string {
  return `whatsapp.templates.manage.wizard.categories.${category.trim().toUpperCase()}`;
}

export function templateDetailsUseCaseTitleKey(
  useCase: TemplateCreationUseCase,
): string {
  return `whatsapp.templates.manage.wizard.useCases.${useCase}.title`;
}

function normalizeButtonKind(raw: string): TemplateListDetailsButtonKind {
  switch (raw) {
    case "QUICK_REPLY":
      return "QUICK_REPLY";
    case "URL":
      return "URL";
    case "PHONE_NUMBER":
    case "PHONE":
      return "PHONE_NUMBER";
    case "VOICE_CALL":
      return "VOICE_CALL";
    default:
      return "OTHER";
  }
}

function buttonTypeLabelKey(kind: TemplateListDetailsButtonKind): string {
  return `whatsapp.templates.list.detailPanel.buttonTypes.${kind}`;
}

function formatPhoneForDetails(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
}

function buildDetailsButtonsFromMeta(
  components: unknown,
): { buttons: TemplateListDetailsButton[]; unhandledButton: boolean } {
  if (!Array.isArray(components)) return { buttons: [], unhandledButton: false };

  const buttons: TemplateListDetailsButton[] = [];
  let unhandledButton = false;

  for (const [componentIndex, entry] of components.entries()) {
    const component = asRecord(entry);
    if (!component || componentType(component) !== "BUTTONS") continue;
    if (!Array.isArray(component.buttons)) continue;

    for (const [buttonIndex, buttonEntry] of component.buttons.entries()) {
      const button = asRecord(buttonEntry);
      if (!button) {
        unhandledButton = true;
        continue;
      }
      const rawType =
        typeof button.type === "string" ? button.type.trim().toUpperCase() : "OTHER";
      const kind = normalizeButtonKind(rawType);
      const label =
        typeof button.text === "string" && button.text.trim().length > 0
          ? button.text.trim()
          : rawType;
      const key = `${componentIndex}-${buttonIndex}-${kind}`;

      if (kind === "URL") {
        const url = typeof button.url === "string" ? button.url.trim() : "";
        buttons.push({
          key,
          kind,
          typeLabelKey: buttonTypeLabelKey(kind),
          label,
          ...(url ? { secondary: url, secondaryKind: "url" as const } : {}),
        });
        continue;
      }

      if (kind === "PHONE_NUMBER") {
        const rawPhone =
          typeof button.phone_number === "string"
            ? button.phone_number
            : typeof button.phoneNumber === "string"
              ? button.phoneNumber
              : "";
        const phone = formatPhoneForDetails(rawPhone);
        buttons.push({
          key,
          kind,
          typeLabelKey: buttonTypeLabelKey(kind),
          label,
          ...(phone ? { secondary: phone, secondaryKind: "phone" as const } : {}),
        });
        continue;
      }

      if (kind === "VOICE_CALL") {
        const ttlRaw =
          typeof button.ttl_minutes === "number"
            ? button.ttl_minutes
            : typeof button.ttlMinutes === "number"
              ? button.ttlMinutes
              : null;
        buttons.push({
          key,
          kind,
          typeLabelKey: buttonTypeLabelKey(kind),
          label,
          ...(ttlRaw !== null
            ? {
                secondary: String(voiceCallTtlMinutesToDays(ttlRaw)),
                secondaryKind: "ttl" as const,
              }
            : {}),
        });
        continue;
      }

      if (kind === "QUICK_REPLY") {
        buttons.push({
          key,
          kind,
          typeLabelKey: buttonTypeLabelKey(kind),
          label,
        });
        continue;
      }

      unhandledButton = true;
      buttons.push({
        key,
        kind: "OTHER",
        typeLabelKey: buttonTypeLabelKey("OTHER"),
        label,
        secondary: rawType,
        secondaryKind: "raw",
      });
    }
  }

  return { buttons, unhandledButton };
}

function buildDetailsContentBlocks(
  components: unknown,
): { blocks: TemplateListDetailsContentBlock[]; hasUnhandled: boolean } {
  if (!Array.isArray(components)) return { blocks: [], hasUnhandled: false };

  const blocks: TemplateListDetailsContentBlock[] = [];
  let hasUnhandled = false;

  for (const [index, entry] of components.entries()) {
    const component = asRecord(entry);
    if (!component) {
      hasUnhandled = true;
      continue;
    }
    const type = componentType(component);

    if (type === "HEADER") {
      const format = headerFormat(component);
      const text = typeof component.text === "string" ? component.text : "";
      blocks.push({
        key: `header-${index}`,
        kind: "header",
        text,
        meta: format !== "TEXT" ? format : undefined,
      });
      continue;
    }

    if (type === "BODY") {
      const text = typeof component.text === "string" ? component.text : "";
      blocks.push({ key: `body-${index}`, kind: "body", text });
      continue;
    }

    if (type === "FOOTER") {
      const text = typeof component.text === "string" ? component.text : "";
      blocks.push({ key: `footer-${index}`, kind: "footer", text });
      continue;
    }

    if (type === "BUTTONS") {
      continue;
    }

    hasUnhandled = true;
    const texts = collectComponentTexts(component);
    blocks.push({
      key: `other-${index}`,
      kind: "other",
      text: texts.join(" · ") || type,
      meta: type,
    });
  }

  return { blocks, hasUnhandled };
}

/** Polished expanded-details model for the templates list (not used for mutations). */
export function buildTemplateListDetailsModel(
  template: WhatsAppTemplateDto,
): TemplateListDetailsModel {
  const formState = templateDtoToFormState(template);
  const { blocks, hasUnhandled: contentUnhandled } = buildDetailsContentBlocks(
    template.components,
  );
  const { buttons, unhandledButton } = buildDetailsButtonsFromMeta(template.components);
  const useCase = resolveTemplateDetailsUseCase(template);

  return {
    preview: buildFormTemplatePresentation(formState),
    parameterFormat: formState.parameterFormat,
    languageLabel: templateDetailsLanguageLabel(template.language),
    categoryLabelKey: templateDetailsCategoryLabelKey(template.category),
    useCaseTitleKey: useCase ? templateDetailsUseCaseTitleKey(useCase) : null,
    variableTypeFieldKey: templateDetailsVariableTypeFieldKey(),
    variableTypeValueKey: templateDetailsVariableTypeValueKey(formState.parameterFormat),
    contentBlocks: blocks,
    buttons,
    hasUnhandledComponents: contentUnhandled || unhandledButton,
    editSurface: resolveTemplateEditSurface(template),
  };
}
